import { useTranslate } from "@tolgee/react";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "#/components/ui/tooltip";
import { useNeedsDepositToTrading } from "#/hooks/use-needs-deposit-to-trading";
import { cn } from "#/lib/utils";
import {
	useDepositInTransit,
	useDepositJustCompleted,
} from "./hooks/use-deposit-in-transit";

type Props = {
	className?: string;
	/**
	 * Extra classes for the status icons (crediting spinner / completed check)
	 * only — pass `text-primary-foreground` when the indicator sits on a
	 * `bg-primary` button, where the default `text-primary` is invisible. Kept
	 * separate from `className` so the triangle's denotive orange (an
	 * arbitrary-value class tailwind-merge can't dedupe) is never overridden.
	 */
	statusClassName?: string;
};

/**
 * Self-gating deposit status icon: a crediting spinner while a deposit is in
 * transit — kept alive even after `needsDeposit` flips false (a full-wallet
 * deposit zeroes the wallet balance mid-crediting) — then a "completed" check
 * for a short window once it lands, a warning triangle while the wallet is
 * funded but the trading account is empty, otherwise null.
 * Don't wrap call sites in `needsDeposit &&`.
 */
export function DepositWarningIndicator({ className, statusClassName }: Props) {
	const { t } = useTranslate();
	const needsDeposit = useNeedsDepositToTrading();
	const inTransit = useDepositInTransit();
	const justCompleted = useDepositJustCompleted();

	if (!inTransit && !justCompleted && !needsDeposit) return null;

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className="inline-flex">
					{inTransit ? (
						<Loader2
							className={cn(
								"h-3.5 w-3.5 shrink-0 animate-spin text-primary",
								className,
								statusClassName,
							)}
						/>
					) : justCompleted ? (
						<Check
							className={cn(
								"h-3.5 w-3.5 shrink-0 text-primary",
								className,
								statusClassName,
							)}
						/>
					) : (
						<AlertTriangle
							className={cn(
								"h-3.5 w-3.5 shrink-0 text-[var(--color-theme-denotives-orange)]",
								className,
							)}
						/>
					)}
				</span>
			</TooltipTrigger>
			<TooltipContent side="top" className="max-w-[240px]">
				{t(
					inTransit
						? "deposit.crediting-title"
						: justCompleted
							? "deposit.title-step3"
							: "deposit.warning.wallet-funded-trading-empty",
				)}
			</TooltipContent>
		</Tooltip>
	);
}
